import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Slim trial countdown banner — the always-visible nudge that turns 14-day
 * trials into paying customers. Three-tier urgency:
 *   >7d  → neutral orange (calm reminder)
 *   4-7d → amber (mid-trial nudge)
 *   ≤3d  → red + pulsing dot (last-chance urgency)
 */
export default function TrialBanner() {
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/pricing/subscription")
      .then((r) => {
        if (!cancelled) setState(r.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!state?.is_trialing) return null;
  const days = state.trial_days_left || 0;

  let tier;
  if (days <= 3) {
    tier = {
      wrap: "bg-red-500/12 border-red-500/40 text-red-200",
      strong: "text-red-300",
      dot: "bg-red-400 animate-pulse",
      icon: "text-red-400",
      cta: "border-red-400/60 text-red-100 hover:bg-red-500/25",
      ctaLabel: "Upgrade now",
      testid: "trial-banner-urgent",
    };
  } else if (days <= 7) {
    tier = {
      wrap: "bg-amber-500/10 border-amber-500/40 text-amber-200",
      strong: "text-amber-300",
      dot: "bg-amber-400",
      icon: "text-amber-400",
      cta: "border-amber-400/60 text-amber-100 hover:bg-amber-500/25",
      ctaLabel: "Upgrade",
      testid: "trial-banner-amber",
    };
  } else {
    tier = {
      wrap: "bg-orange-500/10 border-orange-500/40 text-orange-200",
      strong: "text-orange-300",
      dot: "bg-orange-400",
      icon: "text-orange-400",
      cta: "border-orange-400/60 text-orange-100 hover:bg-orange-500/25",
      ctaLabel: "Upgrade",
      testid: "trial-banner-neutral",
    };
  }

  return (
    <div
      data-testid="trial-banner"
      data-tier={tier.testid}
      className={`w-full text-[12px] font-mono px-4 py-2 flex items-center justify-center gap-3 border-b ${tier.wrap}`}
    >
      <span className="relative inline-flex items-center">
        <span className={`inline-block w-2 h-2 rounded-full ${tier.dot}`} data-testid="trial-banner-dot" />
      </span>
      <Clock size={13} weight="fill" className={tier.icon} />
      <span data-testid="trial-banner-copy">
        <strong className={tier.strong}>Pro trial</strong> · <span data-testid="trial-banner-days">{days}</span> day{days === 1 ? "" : "s"} left · Full access to unlimited discovery + AI outreach
      </span>
      <Link
        to="/pricing"
        data-testid="trial-banner-upgrade"
        className={`inline-flex items-center gap-1 rounded-sm px-2 py-0.5 border ${tier.cta} transition-colors uppercase text-[10px] tracking-wider`}
      >
        {tier.ctaLabel} <ArrowRight size={11} weight="bold" />
      </Link>
    </div>
  );
}
